/**
 * The child shell.
 *
 * Four things are true of everything inside it: no score is ever shown
 * (invariant 1), no URL carries a child identifier (invariant 2), the network
 * state is stated rather than implied (§8.3), and the navigation is at the
 * bottom, where a thumb is.
 *
 * **Le journal n'est plus bloquant** (Hedi, 30 septembre 2026 : *« Je ne veux
 * plus que l'entrée du journal soit bloquante pour naviguer ailleurs dans
 * l'application pour les enfants. »*). Du 3 au 30 septembre, un journal non
 * envoyé redirigeait le travail, la séance, la révision, le positionnement et
 * les séries vers `/eleve`. Du 18 au 29 septembre, le récapitulatif de 21 h l'a
 * dit absent presque tous les soirs pour les deux enfants : le blocage
 * n'obtenait pas le journal, il retenait l'enfant loin des exercices, ce qui est
 * l'inverse du but. « Ma journée » reste le
 * premier écran et le récapitulatif de 21 h dit toujours au parent quand le
 * journal manque ; plus rien ne le réclame avant de laisser travailler.
 */

import { NavLink, Outlet } from "react-router-dom";

import { useSession } from "@/app/session";
import { InstallPrompt } from "@/offline/InstallPrompt";
import { NetworkBanner } from "@/ui/NetworkBanner";

// « Révision d'été » a quitté cette barre le 2 septembre 2026, à la demande de
// Hedi : l'école a repris, et un onglet qui dit « été » à un enfant qui rentre de
// cours se lit comme un reste, pas comme une proposition. L'écran et sa route
// existent toujours — `/eleve/revision` répond — parce qu'il reste le chemin le
// plus court entre un enfant et un exercice, utile pour vérifier une chaîne de
// bout en bout sans monter une séance. Il n'est simplement plus proposé.
const TABS = [
  { to: "/eleve", label: "Ma journée", end: true },
  { to: "/eleve/travail", label: "Mon travail", end: false },
  { to: "/eleve/seance", label: "Séance", end: false },
  { to: "/eleve/progression", label: "Ma progression", end: false },
];

/**
 * « Ma carte » ne s'ajoute qu'au profil lycée (web-pwa §5.9).
 *
 * **Sur le niveau et non sur le prénom**, comme tout ce qui distingue les deux
 * enfants : la carte entière est proposée parce qu'on est en terminale, pas
 * parce qu'on est Mehdi. Cinq onglets sur une barre de téléphone tiennent ; sur
 * la tablette d'une élève de 5ᵉ, trois cents lignes disant « pas encore vue »
 * seraient un mur, et le contrat interdit de mettre la lenteur en accusation.
 *
 * La route `/eleve/carte` répond quand même pour les deux : ce sont les données
 * de l'enfant, sur l'enfant. Ce qui se règle ici est ce qu'on propose.
 */
export function ongletsPour(profile: { fullMap: boolean; ownTraining: boolean }) {
  return [
    ...TABS,
    ...(profile.fullMap ? [{ to: "/eleve/carte", label: "Ma carte", end: false }] : []),
    // « Entraînement » (Hedi, 5 octobre 2026) : l'élève compose ses propres
    // feuilles, depuis les cahiers ou par génération, et les clôt quand il veut
    // lire la correction. **Sur le niveau et non sur le prénom**, pour la même
    // raison que la carte : composer sa révision est un geste de lycéen, et une
    // élève de 5ᵉ reçoit la feuille que son parent a préparée.
    ...(profile.ownTraining
      ? [{ to: "/eleve/entrainement", label: "Entraînement", end: false }]
      : []),
  ];
}

export function ChildShell() {
  const { profile, me } = useSession();

  return (
    <div className="shell shell--child" data-density={profile.density} data-device={profile.device}>
      <NetworkBanner />
      <InstallPrompt device={profile.device} />
      <div className="shell__body">
        <Outlet />
      </div>
      <nav className="tabbar" aria-label={`Navigation de ${me?.display_name ?? "l'élève"}`}>
        {ongletsPour(profile).map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) => `tabbar__item ${isActive ? "is-active" : ""}`}
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
